import { useState } from "react";
import { Loader as Loader2 } from "lucide-react";
import { useContacts } from "@/hooks/useContacts";
import { useAssistants } from "@/hooks/useAssistants";
import { useTeam } from "@/hooks/useTeam";
import { useAuth } from "@/contexts/AuthContext";
import { clampAttributionIdForSave } from "@/lib/chatContactScope";
import {
  COUNTRY_CALLING_CODES,
  DEFAULT_COUNTRY_CALLING_CODE,
  getCountryCallingCodeMobileLabel,
} from "@/data/countryCallingCodes";
import { useTranslation } from "react-i18next";
import { MobileSheet } from "./MobileSheet";
import { MobileSelect, MobileFieldLabel, mobileInputClass } from "./MobileSelect";

/** Monta o JID do WhatsApp igual ao fluxo do web (AddNewContact). */
const buildJid = (phone: string, countryCode: string) => {
  const digits = phone.replace(/\D/g, "");
  const withCountry = digits.startsWith(countryCode) ? digits : `${countryCode}${digits}`;
  return { jid: `${withCountry}@s.whatsapp.net`, phone: withCountry };
};

const NewContactForm = ({ onCreated }: { onCreated: () => void }) => {
  const { t } = useTranslation("crm");
  const { user } = useAuth();
  const { createContact } = useContacts();
  const { assistants } = useAssistants();
  const { members } = useTeam();

  const canAssignToOthers = user?.can_assign_contacts === true || user?.type === "agency_user";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CALLING_CODE);
  const [assistantId, setAssistantId] = useState("none");
  const [attributionId, setAttributionId] = useState(
    canAssignToOthers ? "none" : user?.auth_user_id || "none"
  );
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "error" | "success"; text: string } | null>(null);

  const handleSave = async () => {
    if (!name.trim()) {
      setFeedback({ type: "error", text: t("newContact.nameRequiredError") });
      return;
    }
    if (!phone.trim()) {
      setFeedback({ type: "error", text: t("newContact.phoneRequiredError") });
      return;
    }

    setSaving(true);
    setFeedback(null);

    const { jid, phone: fullPhone } = buildJid(phone, countryCode);
    const selectedAssistant = assistantId === "none" ? null : assistantId;

    const result = await createContact({
      jid,
      phone: fullPhone,
      display_name: name.trim(),
      name: name.trim(),
      push_name_last: name.trim(),
      email: email.trim() || "",
      is_group: false,
      account_assistant_id: selectedAssistant,
      assistant_is_active: !!selectedAssistant,
      attribution_id: clampAttributionIdForSave(
        attributionId === "none" ? null : attributionId,
        user ?? null
      ),
      last_message_id: null,
      last_message_at: null,
      profile_picture: null,
      profile_picture_updated_at: null,
      countryCallingCode: countryCode,
    });

    setSaving(false);

    if (result.success) {
      setFeedback({ type: "success", text: "Contato criado com sucesso!" });
      setTimeout(onCreated, 900);
    } else {
      setFeedback({ type: "error", text: result.error || "Não foi possível criar o contato." });
    }
  };

  return (
    <div className="flex flex-col gap-4 pb-2">
      {feedback && (
        <p
          className={`rounded-xl px-3 py-2.5 text-[13px] ${
            feedback.type === "success"
              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "bg-red-500/10 text-red-600 dark:text-red-400"
          }`}
        >
          {feedback.text}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <MobileFieldLabel>{t("common.nameRequired")}</MobileFieldLabel>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("common.contactNamePlaceholder")}
          className={mobileInputClass}
        />
      </div>

      <div className="flex flex-col gap-2">
        <MobileFieldLabel>{t("common.phoneRequired")}</MobileFieldLabel>
        <div className="flex gap-2">
          <div className="w-[118px] shrink-0">
            <MobileSelect value={countryCode} onChange={setCountryCode} ariaLabel={t("newContact.countryCodeAria")}>
              {COUNTRY_CALLING_CODES.map((country) => (
                <option key={`${country.code}-${country.name}`} value={country.code}>
                  {getCountryCallingCodeMobileLabel(country)}
                </option>
              ))}
            </MobileSelect>
          </div>
          <input
            type="tel"
            inputMode="numeric"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
            placeholder="11999999999"
            className={mobileInputClass}
          />
        </div>
        <p className="text-[12px] text-muted-foreground">{t("newContact.mobilePhoneHint")}</p>
      </div>

      <div className="flex flex-col gap-2">
        <MobileFieldLabel>{t("common.email")}</MobileFieldLabel>
        <input
          type="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("newContact.emailPlaceholder")}
          className={mobileInputClass}
        />
      </div>

      <div className="flex flex-col gap-2">
        <MobileFieldLabel>{t("common.assistant")}</MobileFieldLabel>
        <MobileSelect value={assistantId} onChange={setAssistantId} ariaLabel={t("common.assistant")}>
          <option value="none">{t("common.noAssistant")}</option>
          {assistants
            .filter((a) => a.is_active)
            .map((assistant) => (
              <option key={assistant.id} value={assistant.id}>
                {assistant.name}
              </option>
            ))}
        </MobileSelect>
      </div>

      <div className="flex flex-col gap-2">
        <MobileFieldLabel>{t("newContact.assignTo")}</MobileFieldLabel>
        <MobileSelect
          value={attributionId}
          onChange={setAttributionId}
          disabled={!canAssignToOthers}
          ariaLabel={t("newContact.assignedUserAria")}
        >
          <option value="none">{t("common.noOwner")}</option>
          {members
            .filter((m) => m.is_active && m.auth_user_id)
            .filter((m) => (canAssignToOthers ? true : m.auth_user_id === user?.auth_user_id))
            .map((member) => (
              <option key={member.auth_user_id} value={member.auth_user_id!}>
                {member.name || member.email}
              </option>
            ))}
        </MobileSelect>
        {!canAssignToOthers && (
          <p className="text-[12px] text-muted-foreground">
            {t("common.onlySelf")}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="mt-1 flex h-12 items-center justify-center gap-2 rounded-xl bg-accent-blue text-[15px] font-semibold text-white transition-opacity active:opacity-80 disabled:opacity-50"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
        {saving ? t("newContact.saving") : t("newContact.create")}
      </button>
    </div>
  );
};

interface MobileNewContactSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MobileNewContactSheet = ({ open, onOpenChange }: MobileNewContactSheetProps) => {
  const { t } = useTranslation("crm");

  return (
    <MobileSheet open={open} onOpenChange={onOpenChange} title={t("newContact.button")}>
      {open && <NewContactForm onCreated={() => onOpenChange(false)} />}
    </MobileSheet>
  );
};
